import { Box, Divider } from '@chakra-ui/react';
import { useContext, useState } from 'react';

import Button from '../../../../components/ui/buttons/Button';
import { DealMediaTypeEnum } from '../../../../gql/generated/graphql';
import { MediaContext, MediaContextType } from '../../../../hooks/useMediaCollection';
import { sameEmail } from '../../../../utils/customer';
import { capitalizeWords } from '../../../../utils/strings';
import { DealWithFarthestState } from '../../utils';
import DocumentsCardShell from './DocumentsCardShell';
import PersonToggle, { Person } from './PersonToggle';
import PriorityDocRow from './PriorityDocRow';
import type { UploadingTarget } from './StepperDocumentsCard';
import UploadHistorySection from './UploadHistorySection';
import { getPriorityDocs } from './utils';

type Props = {
  deal: DealWithFarthestState;
  onUpload: (target: UploadingTarget) => void;
};

const PreSigningDocumentsCard = ({ deal, onUpload }: Props) => {
  const [selectedPerson, setSelectedPerson] = useState<Person>('buyer');
  const { media, customerUploadedMedia } = useContext<MediaContextType>(MediaContext);

  const showToggle = !!deal?.cobuyer && sameEmail(deal?.customer, deal?.cobuyer);
  const fallbackPerson: Person = deal?.isCobuyer ? 'cobuyer' : 'buyer';
  const activePerson: Person = showToggle ? selectedPerson : fallbackPerson;
  const isCobuyer = activePerson === 'cobuyer';
  const priorityDocs = getPriorityDocs(activePerson);

  const buyerName =
    capitalizeWords(
      `${deal?.customer?.first_name ?? ''} ${deal?.customer?.last_name ?? ''}`.trim(),
    ) || 'Buyer';
  const cobuyerName =
    capitalizeWords(
      `${deal?.cobuyer?.first_name ?? ''} ${deal?.cobuyer?.last_name ?? ''}`.trim(),
    ) || 'Co-Buyer';

  const uploadedTypes = new Set(media.map((mediaItem) => mediaItem.type as DealMediaTypeEnum));
  const allUploaded = priorityDocs.every(
    (doc) => uploadedTypes.has(doc.front) && (!doc.back || uploadedTypes.has(doc.back)),
  );

  const subcopy = allUploaded
    ? 'Looking good! All priority documents uploaded.'
    : 'Start uploading your documents';

  const toggle = showToggle ? (
    <PersonToggle
      activePerson={activePerson}
      setSelectedPerson={setSelectedPerson}
      buyerName={buyerName}
      cobuyerName={cobuyerName}
    />
  ) : undefined;

  return (
    <DocumentsCardShell subcopy={subcopy} toggle={toggle}>
      {priorityDocs.map((doc) => (
        <PriorityDocRow
          key={doc.label}
          doc={doc}
          uploadedTypes={uploadedTypes}
          onUpload={(priorityDoc) => onUpload({ doc: priorityDoc, isCobuyer })}
        />
      ))}
      <Divider mb={4} bgColor="grayDarkBackground" />
      <Button
        variant="link"
        type="button"
        size="sm"
        fontWeight="bold"
        fontSize="12px"
        onClick={() => onUpload({ isCobuyer })}
        display="block"
        mx="auto"
      >
        UPLOAD OTHER DOCUMENTS
      </Button>
      {customerUploadedMedia.length > 0 && (
        <Box mt={4}>
          <Divider mb={4} bgColor="grayDarkBackground" />
          <UploadHistorySection />
        </Box>
      )}
    </DocumentsCardShell>
  );
};

export default PreSigningDocumentsCard;
